import { startTransition, type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate, type LinkProps, type To } from 'react-router-dom';
import { prefetchSettingsMaster } from './settingsPrefetch';

function toPath(to: To): string {
  if (typeof to === 'string') return to;
  if (typeof to === 'object' && to != null && 'pathname' in to) {
    return `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
  }
  return '';
}

type SettingsLinkProps = LinkProps & {
  children: ReactNode;
};

/**
 * Settings navigation link: prefetches the master chunk on hover/focus and
 * navigates inside startTransition so the previous screen stays visible while
 * the next lazy page loads (instead of an immediate blank skeleton).
 */
export function SettingsLink({ to, onClick, children, ...rest }: SettingsLinkProps) {
  const navigate = useNavigate();
  const path = toPath(to);

  const warm = () => {
    if (path) prefetchSettingsMaster(path);
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (event.button !== 0) return;
    event.preventDefault();
    warm();
    startTransition(() => {
      navigate(to);
    });
  };

  return (
    <Link
      to={to}
      {...rest}
      onMouseEnter={warm}
      onFocus={warm}
      onTouchStart={warm}
      onPointerDown={warm}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
}
